/**
 * Progression — the typed campaign structure (design §16). One per game
 * (`design/progression.json`): which level is "level 1" and how the rest unlock.
 * Validation reuses the lock-and-key fixpoint at campaign scale: an entry unlocks only
 * when its required entries (and its set) are already unlockable.
 */
import { z } from 'zod';
import type { LevelFinding } from './types.js';

export const ProgressionSchema = z.object({
  /** linear = play in order; gated = per-entry requirements; sets = worlds; open = pick any. */
  mode: z.enum(['linear', 'gated', 'sets', 'open']),
  /** Can finished levels be replayed? */
  replay: z.boolean().default(true),
  entries: z
    .array(
      z.object({
        levelId: z.string().min(1),
        /** Display label ("1", "W2·3"). Defaults to the 1-based position. */
        label: z.string().optional(),
        /** Set/world id (sets mode). */
        set: z.string().optional(),
        unlock: z
          .object({
            /** Level ids that must be completed first. */
            requires: z.array(z.string()).default([]),
            /** Optional star/currency threshold (assumed earnable). */
            stars: z.number().int().positive().optional(),
          })
          .optional(),
      }),
    )
    .default([]),
  sets: z
    .array(
      z.object({
        id: z.string().min(1),
        name: z.string().optional(),
        unlock: z.object({ requires: z.array(z.string()).default([]) }).optional(), // set ids
      }),
    )
    .default([]),
});

export type Progression = z.infer<typeof ProgressionSchema>;
export type ProgressionEntry = Progression['entries'][number];

export const DEFAULT_PROGRESSION: Progression = {
  mode: 'linear',
  replay: true,
  entries: [],
  sets: [],
};

/**
 * Validate a progression against the game's existing level ids. Pure + deterministic.
 * Linear mode implies each entry requires the previous one (explicit `requires` ignored).
 */
export function validateProgression(
  prog: Progression,
  existingLevelIds: ReadonlySet<string>,
): LevelFinding[] {
  const findings: LevelFinding[] = [];
  const entryIds = new Set<string>();

  prog.entries.forEach((e, i) => {
    if (entryIds.has(e.levelId)) {
      findings.push({
        severity: 'error',
        code: 'progression-duplicate-entry',
        message: `level ${e.levelId} appears twice in the progression`,
        at: { levelId: e.levelId },
      });
    }
    entryIds.add(e.levelId);
    if (!existingLevelIds.has(e.levelId)) {
      findings.push({
        severity: 'error',
        code: 'progression-missing-level',
        message: `progression entry ${i + 1} references a level that doesn't exist: ${e.levelId}`,
        at: { levelId: e.levelId },
      });
    }
    for (const req of e.unlock?.requires ?? []) {
      if (!prog.entries.some((x) => x.levelId === req)) {
        findings.push({
          severity: 'error',
          code: 'progression-unknown-requirement',
          message: `${e.levelId} requires ${req}, which isn't in the progression`,
          at: { levelId: e.levelId },
        });
      }
    }
    if (e.set && !prog.sets.some((s) => s.id === e.set)) {
      findings.push({
        severity: 'error',
        code: 'progression-unknown-set',
        message: `${e.levelId} belongs to set ${e.set}, which isn't defined`,
        at: { levelId: e.levelId },
      });
    }
  });

  for (const s of prog.sets) {
    for (const req of s.unlock?.requires ?? []) {
      if (!prog.sets.some((x) => x.id === req)) {
        findings.push({
          severity: 'error',
          code: 'progression-unknown-set',
          message: `set ${s.id} requires unknown set ${req}`,
        });
      }
    }
  }

  // Unlockability fixpoint (gated/sets; linear is an implicit chain; open unlocks all).
  if (prog.entries.length > 0 && (prog.mode === 'gated' || prog.mode === 'sets')) {
    const unlockedSets = new Set<string>();
    const unlocked = new Set<string>();
    let grew = true;
    while (grew) {
      grew = false;
      for (const s of prog.sets) {
        if (unlockedSets.has(s.id)) continue;
        if ((s.unlock?.requires ?? []).every((r) => unlockedSets.has(r))) {
          unlockedSets.add(s.id);
          grew = true;
        }
      }
      for (const e of prog.entries) {
        if (unlocked.has(e.levelId)) continue;
        const reqsOk = (e.unlock?.requires ?? []).every((r) => unlocked.has(r));
        const setOk = !e.set || unlockedSets.has(e.set);
        if (reqsOk && setOk) {
          unlocked.add(e.levelId);
          grew = true;
        }
      }
    }
    for (const e of prog.entries) {
      if (!unlocked.has(e.levelId)) {
        findings.push({
          severity: 'error',
          code: 'level-never-unlockable',
          message: `${e.levelId} can never be unlocked (circular or unsatisfiable requirements)`,
          at: { levelId: e.levelId },
        });
      }
    }
  }

  // Levels that exist but aren't placed anywhere.
  for (const id of existingLevelIds) {
    if (!entryIds.has(id)) {
      findings.push({
        severity: 'warn',
        code: 'level-not-in-progression',
        message: `level ${id} isn't placed in the progression`,
        at: { levelId: id },
      });
    }
  }

  return findings;
}
